import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  AlertCircle, Wind, Activity, ChevronRight, ArrowRight, CheckCircle, 
  Layers, Waves, Heart, Youtube, Brain, Sparkles, Target, ClipboardList, Droplets, Trophy 
} from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import ReviewsSection from '@/components/website/ReviewsSection';

/* eslint-disable react/no-unescaped-entities */

// V3 Theme Colors - Aquatic & Breathing (Based on reference mockup)
const C = {
  // Deep ocean backgrounds (from reference image)
  deepBlue: '#0A1931',      // darkest blue
  oceanDark: '#2C3E50',     // mid-tone blue
  oceanMid: '#4A69BD',      // lighter blue with purple hint
  
  // Cyan/teal accents — aligned with homepage sky blue (#0EA5E9)
  cyan: '#0284C7',
  cyanLight: '#0EA5E9',
  cyanGlow: 'rgba(14, 165, 233, 0.5)',
  
  // Horizon glow color
  horizonGlow: '#E0F7FA',
  
  // Blue glacier
  glacier: '#7DD3FC',
  glacierLight: '#BAE6FD',
  
  // Text colors — dark tones for readability on light aquatic bg (matches CabinetHomePage)
  white: '#0F172A',         // was pure white — now slate-900 for readable headlines
  textPrimary: '#0F172A',   // slate-900
  textSecondary: '#334155', // slate-700
  textMuted: '#64748B',     // slate-500
  
  // Glass effects (for app card)
  glassBg: 'rgba(255, 255, 255, 0.12)',
  glassBorder: 'rgba(255, 255, 255, 0.2)',
  glassHover: 'rgba(255, 255, 255, 0.18)',
};

// Rising bubble component
const RisingBubble = ({ delay = 0, size = 8, left, duration = 15 }) => (
  <div
    className="absolute rounded-full pointer-events-none"
    style={{
      width: size,
      height: size,
      left: `${left}%`,
      bottom: '-5%',
      background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.4) 0%, rgba(6,182,212,0.2) 50%, transparent 70%)`,
      border: '1px solid rgba(255,255,255,0.2)',
      animation: `bubble-rise ${duration}s ease-in-out ${delay}s infinite`,
    }}
  />
);

// Floating particles for aquatic effect
const FloatingParticle = ({ delay = 0, size = 4, left, duration = 8 }) => (
  <div
    className="absolute rounded-full pointer-events-none"
    style={{
      width: size,
      height: size,
      left: `${left}%`,
      bottom: '-5%',
      background: `radial-gradient(circle, ${C.cyanLight} 0%, transparent 70%)`,
      filter: 'blur(1px)',
      animation: `particle-float ${duration}s linear ${delay}s infinite`,
    }}
  />
);

// Aurora effect for background
const AuroraWave = ({ className, delay = 0 }) => (
  <div
    className={`absolute pointer-events-none ${className}`}
    style={{
      width: '150%',
      height: '300px',
      background: 'linear-gradient(90deg, transparent, rgba(6,182,212,0.1), rgba(34,211,238,0.15), rgba(6,182,212,0.1), transparent)',
      filter: 'blur(40px)',
      animation: `aurora 20s ease-in-out ${delay}s infinite`,
      transform: 'skewX(-15deg)',
    }}
  />
);

// Water caustics overlay
const WaterCaustics = () => (
  <div 
    className="absolute inset-0 pointer-events-none overflow-hidden"
    style={{
      backgroundImage: `
        radial-gradient(ellipse 100px 100px at 20% 30%, rgba(6,182,212,0.08) 0%, transparent 50%),
        radial-gradient(ellipse 150px 80px at 70% 60%, rgba(34,211,238,0.06) 0%, transparent 50%),
        radial-gradient(ellipse 80px 120px at 40% 80%, rgba(6,182,212,0.05) 0%, transparent 50%),
        radial-gradient(ellipse 120px 90px at 80% 20%, rgba(34,211,238,0.07) 0%, transparent 50%)
      `,
      animation: 'caustics 15s ease-in-out infinite',
    }}
  />
);

// Underwater fog/mist layer - more dramatic
const UnderwaterFog = () => (
  <div className="absolute inset-0 pointer-events-none">
    {/* Atmospheric clouds/mist texture */}
    <div 
      className="absolute inset-0"
      style={{
        background: 'radial-gradient(ellipse 100% 80% at 50% 20%, rgba(74,105,189,0.15) 0%, transparent 60%)',
        filter: 'blur(80px)',
      }}
    />
    {/* Bottom deep atmosphere */}
    <div 
      className="absolute bottom-0 left-0 right-0 h-[50%]"
      style={{
        background: 'linear-gradient(0deg, rgba(10,25,49,0.9) 0%, rgba(44,62,80,0.4) 50%, transparent 100%)',
        filter: 'blur(30px)',
      }}
    />
  </div>
);

// Dramatic light rays from above - like sunlight through water
const LightRays = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    {/* Main bright light ray - center left */}
    <div 
      className="absolute"
      style={{
        top: '-10%',
        left: '25%',
        width: '400px',
        height: '120%',
        background: 'linear-gradient(180deg, rgba(224,247,250,0.15) 0%, rgba(74,105,189,0.08) 30%, rgba(44,62,80,0.02) 60%, transparent 85%)',
        transform: 'rotate(8deg)',
        filter: 'blur(15px)',
        animation: 'light-ray-breathe 6s ease-in-out infinite',
      }}
    />
    {/* Strong center-right ray */}
    <div 
      className="absolute"
      style={{
        top: '-10%',
        left: '45%',
        width: '350px',
        height: '110%',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(224,247,250,0.08) 25%, rgba(74,105,189,0.03) 55%, transparent 80%)',
        transform: 'rotate(-5deg)',
        filter: 'blur(20px)',
        animation: 'light-ray-breathe 7s ease-in-out infinite 1s',
      }}
    />
    {/* Right ray */}
    <div 
      className="absolute"
      style={{
        top: '-5%',
        right: '10%',
        width: '450px',
        height: '100%',
        background: 'linear-gradient(180deg, rgba(224,247,250,0.1) 0%, rgba(74,105,189,0.05) 35%, transparent 75%)',
        transform: 'rotate(15deg)',
        filter: 'blur(25px)',
        animation: 'light-ray-breathe 8s ease-in-out infinite 2s',
      }}
    />
    {/* Far left subtle ray */}
    <div 
      className="absolute"
      style={{
        top: '-15%',
        left: '5%',
        width: '300px',
        height: '130%',
        background: 'linear-gradient(180deg, rgba(74,105,189,0.08) 0%, rgba(44,62,80,0.03) 40%, transparent 70%)',
        transform: 'rotate(-12deg)',
        filter: 'blur(30px)',
        animation: 'light-ray-breathe 9s ease-in-out infinite 3s',
      }}
    />
  </div>
);

// Glowing horizon effect - bright focal point at bottom
const GlowingHorizon = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    {/* Wide ambient glow - more intense */}
    <div 
      className="absolute bottom-0 left-0 right-0"
      style={{
        height: '70%',
        background: 'radial-gradient(ellipse 180% 120% at 50% 100%, rgba(224,247,250,0.35) 0%, rgba(74,105,189,0.2) 25%, rgba(44,62,80,0.08) 50%, transparent 85%)',
        filter: 'blur(60px)',
        animation: 'horizon-breathe 6s ease-in-out infinite',
      }}
    />
    {/* Bright center focal point - stronger */}
    <div 
      className="absolute bottom-0 left-1/2 -translate-x-1/2"
      style={{
        width: '80%',
        height: '50%',
        background: 'radial-gradient(ellipse 90% 100% at 50% 100%, rgba(255,255,255,0.4) 0%, rgba(224,247,250,0.3) 20%, rgba(74,105,189,0.12) 45%, transparent 80%)',
        filter: 'blur(50px)',
        animation: 'horizon-breathe 5s ease-in-out infinite 0.5s',
      }}
    />
    {/* Core bright spot - very bright */}
    <div 
      className="absolute bottom-[2%] left-1/2 -translate-x-1/2"
      style={{
        width: '50%',
        height: '30%',
        background: 'radial-gradient(ellipse 100% 100% at 50% 90%, rgba(255,255,255,0.35) 0%, rgba(224,247,250,0.25) 30%, transparent 70%)',
        filter: 'blur(35px)',
        animation: 'horizon-breathe 4s ease-in-out infinite 1s',
      }}
    />
  </div>
);

// Vignette overlay - darker edges
const Vignette = () => (
  <div 
    className="fixed inset-0 pointer-events-none z-[1]"
    style={{
      background: 'radial-gradient(ellipse 65% 55% at 50% 50%, transparent 0%, rgba(10,25,49,0.6) 100%)',
    }}
  />
);

// Aquatic texture overlay
const AquaticTexture = () => (
  <div 
    className="absolute inset-0 pointer-events-none opacity-[0.03]"
    style={{
      backgroundImage: `
        repeating-linear-gradient(
          0deg,
          transparent,
          transparent 2px,
          rgba(6,182,212,0.5) 2px,
          rgba(6,182,212,0.5) 4px
        ),
        repeating-linear-gradient(
          90deg,
          transparent,
          transparent 2px,
          rgba(34,211,238,0.3) 2px,
          rgba(34,211,238,0.3) 4px
        )
      `,
      backgroundSize: '60px 60px',
      animation: 'texture-drift 60s linear infinite',
    }}
  />
);

// Breathing glow orb
const BreathingOrb = ({ className, color, delay = 0, size = 400 }) => (
  <div
    className={`absolute rounded-full pointer-events-none ${className}`}
    style={{
      width: size,
      height: size,
      background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
      filter: 'blur(60px)',
      animation: `breathe-slow 8s ease-in-out ${delay}s infinite`,
    }}
  />
);

// Glassmorphism card component with enhanced hover
const GlassCard = ({ children, className = '', hover = true, onClick, glow = false }) => (
  <div
    onClick={onClick}
    className={`
      bg-white/5 backdrop-blur-2xl border border-white/10 
      rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]
      ${hover ? 'hover:bg-white/10 hover:border-cyan-400/40 hover:shadow-[0_8px_40px_rgba(6,182,212,0.15)] transition-all duration-500 cursor-pointer group' : ''}
      ${glow ? 'animate-[glow-pulse_4s_ease-in-out_infinite]' : ''}
      ${className}
    `}
  >
    {children}
  </div>
);

// Philosophy Tagline with auto-scroll (desktop) and static wrapped text (mobile)
const PhilosophyTagline = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [isExpanded, setIsExpanded] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const textRef = useRef(null);
  const containerRef = useRef(null);
  
  const fullText = "Comprendre la santé comme l'équilibre parfait entre tension et relâchement, restaurer l'harmonie par la compréhension, l'écoute du corps, et l'abandon des résistances";
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    // On mobile or expanded, no scrolling animation
    if (isMobile || isExpanded) {
      setScrollPosition(0);
      return;
    }
    
    const textWidth = textRef.current?.scrollWidth || 0;
    const containerWidth = containerRef.current?.offsetWidth || 0;
    const maxScroll = textWidth - containerWidth + 40;
    
    if (maxScroll <= 0) return;
    
    let animationId;
    let startTime;
    const duration = 12000;
    const pauseDuration = 2000;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      
      if (elapsed < pauseDuration) {
        setScrollPosition(0);
        animationId = requestAnimationFrame(animate);
        return;
      }
      
      const scrollElapsed = elapsed - pauseDuration;
      if (scrollElapsed < duration) {
        const progress = scrollElapsed / duration;
        const easeProgress = progress < 0.5 
          ? 2 * progress * progress 
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        setScrollPosition(easeProgress * maxScroll);
        animationId = requestAnimationFrame(animate);
        return;
      }
      
      if (scrollElapsed < duration + pauseDuration) {
        setScrollPosition(maxScroll);
        animationId = requestAnimationFrame(animate);
        return;
      }
      
      startTime = null;
      setScrollPosition(0);
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationId);
  }, [isExpanded, isMobile]);
  
  const handleClick = () => {
    if (isMobile) return;
    setIsExpanded(!isExpanded);
    setScrollPosition(0);
  };
  
  // Mobile: static, wrapped text, max 3 lines with ellipsis
  if (isMobile) {
    return (
      <div className="relative z-20 py-3 text-center bg-white/[0.04] border-b border-white/10">
        <p 
          className="px-4 text-[13px] italic font-medium"
          style={{ 
            color: '#475569', 
            fontFamily: 'Figtree, sans-serif',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: '1.5',
          }}
          data-testid="philosophy-tagline-mobile"
        >
          "{fullText}"
        </p>
      </div>
    );
  }
  
  // Desktop: static, always visible
  return (
    <div 
      ref={containerRef}
      className="relative z-20 py-3 text-center bg-white/[0.04] border-b border-white/10"
    >
      <p 
        ref={textRef}
        className="px-6 text-xs sm:text-sm italic font-medium whitespace-normal"
        style={{ 
          color: '#475569', 
          fontFamily: 'Figtree, sans-serif',
        }}
      >
        "{fullText}"
      </p>
    </div>
  );
};

const HomePageV2 = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // If already logged in, jump straight to the exercises hub;
  // otherwise route through /login so the user can register/authenticate first.
  const goToExercises = () => navigate(user ? '/breathing' : '/login');

  const conditions = [
    {
      icon: AlertCircle,
      title: "Hyperventilation",
      description: "Découvrez comment reconnaître et traiter le syndrome d'hyperventilation chronique.",
      link: "/hyperventilation",
    },
    {
      icon: Wind,
      title: "Respiration Dysfonctionnelle",
      description: "Comprenez les patterns respiratoires inadaptés et comment les corriger.",
      link: "/respiration-dysfonctionnelle",
    },
    {
      icon: Waves,
      title: "Asthme",
      description: "Techniques respiratoires pour gérer et réduire les symptômes de l'asthme.",
      link: "/asthme",
    },
    {
      icon: Activity,
      title: "COVID Long",
      description: "Rééducation respiratoire pour les symptômes persistants post-COVID.",
      link: "/covid-long",
    }
  ];

  const educationalResources = [
    {
      icon: Brain,
      title: "Causes des Troubles Respiratoires",
      description: "Le rôle du système nerveux autonome dans l'apparition des dysfonctionnements respiratoires.",
      link: "/causes-troubles-respiratoires",
    },
    {
      icon: Layers,
      title: "Mécanique Respiratoire",
      description: "Anatomie et physiologie : comprendre comment fonctionne la respiration.",
      link: "/mecanique-respiratoire",
    },
    {
      icon: Sparkles,
      title: "Respiration Optimale",
      description: "Découvrez les 6 principes fondamentaux pour une respiration parfaite et ses bénéfices.",
      link: "/respiration-optimale",
    },
    {
      icon: Target,
      title: "Méthode Buteyko",
      description: "Technique de respiration pour améliorer la tolérance au CO₂ et réduire l'hyperventilation.",
      link: "/methode-buteyko",
    },
    {
      icon: Heart,
      title: "Cohérence Cardiaque",
      description: "Technique scientifique pour harmoniser le cœur, le cerveau et les émotions.",
      link: "/coherence-cardiaque",
    },
    {
      icon: Activity,
      title: "Ma Chaîne YouTube",
      description: "Vidéos d'exercices respiratoires et techniques de rééducation pour patients COVID long.",
      link: "/youtube",
    },
    {
      icon: ClipboardList,
      title: "Évaluer Votre Respiration",
      description: "Tests simples pour évaluer votre mécanique respiratoire, fréquence, score BOLT et score de Nijmegen.",
      link: "/evaluer-respiration",
    }
  ];

  const benefits = [
    "Réduction des symptômes respiratoires",
    "Amélioration de la tolérance à l'effort",
    "Diminution de l'anxiété",
    "Meilleur sommeil",
    "Équilibre du système nerveux",
    "Augmentation de l'énergie",
    "Réduction de l'inflammation",
    "Amélioration de la concentration"
  ];

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{
        color: C.textPrimary,
        fontFamily: 'Figtree, sans-serif'
      }}
      data-testid="home-page-v2"
    >
      {/* Water background — same as CabinetHomePage */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            "url('https://customer-assets-lqy194kg.emergentagent.net/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/hrao4qok_ChatGPT%20Image%2027%20ao%C3%BBt%202026%2C%2008_49_20.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      {/* Soft veil for legibility over the water photo */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0"
        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(240,247,252,0.85) 55%, rgba(214,235,246,0.75) 100%)' }}
      />
      {/* Watermark logo — very faint, centered, same treatment as home */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-[1] flex items-center justify-center"
        style={{ opacity: 0.045 }}
      >
        <img
          src="https://customer-assets-lqy194kg.emergentagent.net/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/qo8j3b4e_Capture%20d%27%C3%A9cran%202026-08-29%200910226.PNG"
          alt=""
          className="max-w-[70vw] md:max-w-[45vw] w-full h-auto"
        />
      </div>
      {/* Global Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500&family=DM+Sans:wght@400;500;600&display=swap');
        /* Auto-adapt legacy white text to dark tones on the light aquatic bg,
           EXCEPT inside the dark-blue hero panel where white must stay white. */
        [data-testid="home-page-v2"] .text-white:not([data-testid="hero-respiration-sante-banner"] .text-white) { color: #0F172A !important; }
        [data-testid="home-page-v2"] .text-white\\/70:not([data-testid="hero-respiration-sante-banner"] .text-white\\/70) { color: rgba(15,23,42,0.72) !important; }
        [data-testid="home-page-v2"] .text-white\\/60:not([data-testid="hero-respiration-sante-banner"] .text-white\\/60) { color: rgba(15,23,42,0.62) !important; }
        [data-testid="hero-respiration-sante-banner"] .text-white { color: #FFFFFF !important; }
        [data-testid="hero-respiration-sante-banner"] .text-white\\/70 { color: rgba(255,255,255,0.75) !important; }
        [data-testid="hero-respiration-sante-banner"] .text-white\\/60 { color: rgba(255,255,255,0.65) !important; }
        /* Turquoise accents → same homepage blue (#0EA5E9). Preserve white span in the hero title. */
        [data-testid="home-page-v2"] .text-cyan-300,
        [data-testid="home-page-v2"] .text-cyan-400,
        [data-testid="home-page-v2"] .text-cyan-500 { color: #0EA5E9 !important; }
        [data-testid="home-page-v2"] .hover\\:text-cyan-300:hover { color: #0284C7 !important; }
        [data-testid="home-page-v2"] .border-cyan-400\\/40 { border-color: rgba(14,165,233,0.4) !important; }
        [data-testid="hero-respiration-sante-banner"] .text-cyan-300,
        [data-testid="hero-respiration-sante-banner"] .text-cyan-400 { color: #38BDF8 !important; }
      `}</style>

      {/* === LIGHTWEIGHT AQUATIC ACCENTS (BG image already has waves & atmosphere) === */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {/* Rising bubbles - kept for life/motion */}
        <RisingBubble delay={0} size={12} left={5} duration={18} />
        <RisingBubble delay={3} size={8} left={15} duration={15} />
        <RisingBubble delay={6} size={6} left={25} duration={20} />
        <RisingBubble delay={2} size={10} left={35} duration={16} />
        <RisingBubble delay={8} size={7} left={48} duration={19} />
        <RisingBubble delay={1} size={9} left={60} duration={17} />
        <RisingBubble delay={5} size={6} left={72} duration={21} />
        <RisingBubble delay={4} size={11} left={82} duration={14} />
        <RisingBubble delay={7} size={8} left={92} duration={18} />

        {/* Small floating light particles - subtle, don't cover bg */}
        <FloatingParticle delay={0} size={6} left={8} duration={25} />
        <FloatingParticle delay={4} size={4} left={18} duration={20} />
        <FloatingParticle delay={2} size={5} left={30} duration={28} />
        <FloatingParticle delay={6} size={3} left={42} duration={22} />
        <FloatingParticle delay={1} size={6} left={55} duration={26} />
        <FloatingParticle delay={5} size={4} left={68} duration={24} />
        <FloatingParticle delay={3} size={5} left={80} duration={30} />
        <FloatingParticle delay={7} size={4} left={90} duration={23} />
      </div>

      <SiteHeaderV2 />

      {/* ============ PHILOSOPHY TAGLINE ============ */}
      <PhilosophyTagline />

      {/* ============ APP PREVIEW SECTION - Hidden on mobile ============ */}
      <div 
        className="hidden md:block relative z-10 px-6 lg:px-12 py-8"
      >
        {/* Local keyframes for breathing circles */}
        <style>{`
          @keyframes rs-breathe {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.09); }
          }
          @keyframes rs-halo {
            0%, 100% {
              opacity: 0.55;
              transform: translate(-50%, -50%) scale(1);
            }
            50% {
              opacity: 0.85;
              transform: translate(-50%, -50%) scale(1.18);
            }
          }
          @keyframes rs-shadow {
            0%, 100% {
              box-shadow: 0 0 40px rgba(var(--rs-glow), 0.35),
                          0 0 80px rgba(var(--rs-glow), 0.15),
                          inset 0 0 80px rgba(var(--rs-glow), 0.3);
            }
            50% {
              box-shadow: 0 0 65px rgba(var(--rs-glow), 0.55),
                          0 0 130px rgba(var(--rs-glow), 0.25),
                          inset 0 0 100px rgba(var(--rs-glow), 0.45);
            }
          }
          .rs-circle {
            animation: rs-breathe 5s ease-in-out infinite,
                       rs-shadow 5s ease-in-out infinite;
          }
          .rs-halo {
            animation: rs-halo 5s ease-in-out infinite;
          }
          .rs-circle-sky    { --rs-glow: 56, 189, 248; }
          .rs-circle-amber  { --rs-glow: 251, 191, 36; animation-delay: 0.6s, 0.6s; }
          .rs-circle-violet { --rs-glow: 167, 139, 250; animation-delay: 1.2s, 1.2s; }
          .rs-halo-amber  { animation-delay: 0.6s; }
          .rs-halo-violet { animation-delay: 1.2s; }
        `}</style>

        {/* Bright sky-blue panel — matches the logo color palette */}
        <button
          onClick={goToExercises}
          className="group relative block w-full max-w-6xl mx-auto transition-all duration-500 hover:scale-[1.005] focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40 rounded-[2rem]"
          style={{
            padding: '48px 40px 56px',
            background: 'linear-gradient(180deg, #0EA5E9 0%, #0284C7 55%, #075985 100%)',
            border: '1px solid rgba(255,255,255,0.22)',
            boxShadow: `
              inset 0 1px 0 rgba(255,255,255,0.35),
              inset 0 -1px 0 rgba(255,255,255,0.10),
              inset 1px 0 0 rgba(255,255,255,0.15),
              inset -1px 0 0 rgba(255,255,255,0.15),
              0 30px 60px -20px rgba(14,165,233,0.35)
            `,
          }}
          data-testid="hero-respiration-sante-banner"
        >
          {/* Top glass sheen — the tell-tale sign of real glass */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-16 rounded-t-[2rem]"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.10) 0%, transparent 100%)',
              mixBlendMode: 'screen',
            }}
          />
          {/* Title */}
          <h2
            className="relative text-4xl lg:text-5xl font-light text-white text-center mb-2"
            style={{
              fontFamily: 'Outfit, sans-serif',
              textShadow: '0 0 22px rgba(180,230,255,0.4), 0 2px 8px rgba(0,0,0,0.4)',
            }}
          >
            Respiration <span className="text-white/95 font-normal">Santé</span>
          </h2>
          <p className="relative text-white/70 text-center text-sm lg:text-base mb-12 max-w-xl mx-auto">
            Choisissez votre pratique et commencez votre voyage vers une meilleure santé respiratoire
          </p>

          {/* 3 Breathing Circles */}
          <div className="relative grid grid-cols-3 gap-8 lg:gap-16 max-w-4xl mx-auto">
            {[
              { id: 'guided',    title: 'Exercices Guidés',    subtitle: 'Techniques respiratoires scientifiquement prouvées', Icon: Wind,     gradient: 'from-sky-400 to-cyan-500',     variant: 'sky',    haloColor: 'rgba(34, 211, 238, 0.55)' },
              { id: 'challenge', title: 'Défis Respiratoires', subtitle: 'Testez vos limites et battez vos records',           Icon: Trophy,   gradient: 'from-amber-400 to-orange-500', variant: 'amber',  haloColor: 'rgba(251, 191, 36, 0.45)' },
              { id: 'custom',    title: 'Mes Exercices',       subtitle: 'Vos créations personnalisées',                        Icon: Sparkles, gradient: 'from-violet-400 to-purple-500', variant: 'violet', haloColor: 'rgba(167, 139, 250, 0.5)' },
            ].map((ex) => (
              <div key={ex.id} className="relative flex flex-col items-center">
                {/* Breathing blue halo behind circle */}
                <div
                  className={`rs-halo ${ex.variant === 'amber' ? 'rs-halo-amber' : ex.variant === 'violet' ? 'rs-halo-violet' : ''} pointer-events-none absolute top-24 left-1/2 w-56 h-56 lg:w-72 lg:h-72 rounded-full`}
                  style={{
                    background: `radial-gradient(circle at center, ${ex.haloColor} 0%, rgba(34, 211, 238, 0.22) 45%, transparent 75%)`,
                    filter: 'blur(30px)',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 0,
                  }}
                />
                <div
                  className={`rs-circle rs-circle-${ex.variant} relative z-[1] w-32 h-32 lg:w-44 lg:h-44 rounded-full flex items-center justify-center transition-transform duration-500 group-hover:scale-105`}
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.05) 100%)',
                    backdropFilter: 'blur(18px)',
                    WebkitBackdropFilter: 'blur(18px)',
                    border: '1px solid rgba(255,255,255,0.25)',
                  }}
                >
                  <div
                    className={`absolute inset-0 rounded-full bg-gradient-to-br ${ex.gradient} opacity-60`}
                    style={{ mixBlendMode: 'overlay' }}
                  />
                  <div
                    className="absolute inset-2 rounded-full"
                    style={{
                      border: '1px solid rgba(255,255,255,0.18)',
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 55%)',
                    }}
                  />
                  <ex.Icon className="relative z-10 w-12 h-12 lg:w-16 lg:h-16 text-white drop-shadow-lg" strokeWidth={1.5} />
                </div>
                <span
                  className="mt-6 text-lg lg:text-xl font-medium text-white text-center transition-colors group-hover:text-cyan-300"
                  style={{ fontFamily: 'Outfit, sans-serif', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}
                >
                  {ex.title}
                </span>
                <span
                  className="mt-2 text-xs lg:text-sm text-white/60 text-center max-w-[220px] leading-relaxed"
                >
                  {ex.subtitle}
                </span>
              </div>
            ))}
          </div>
        </button>
      </div>

      {/* ============ HERO SECTION ============ */}
      <section className="relative pt-12 md:pt-24 lg:pt-32 pb-6 overflow-hidden z-10">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 relative z-10">
          {/* Full width text content */}
          <div className="text-center max-w-4xl mx-auto">
            {/* Mobile: Bouton Exercices au-dessus du titre */}
            <div className="md:hidden mb-6">
              <button
                onClick={goToExercises}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold transition-all duration-500 text-white border shadow-lg hover:shadow-xl hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                style={{
                  background: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 55%, #075985 100%)',
                  borderColor: 'rgba(255,255,255,0.35)',
                  boxShadow: '0 8px 24px -6px rgba(14,165,233,0.55), inset 0 1px 0 rgba(255,255,255,0.30)',
                  textShadow: '0 1px 2px rgba(0,0,0,0.25)',
                }}
                data-testid="mobile-exercises-btn"
              >
                <Wind size={18} color="#FFFFFF" style={{ filter: 'drop-shadow(0 0 6px rgba(180, 230, 255, 0.9))' }} />
                Exercices
              </button>
            </div>
            
            <p 
              className="text-xs md:text-sm uppercase tracking-[0.3em] mb-6 font-semibold"
              style={{ 
                color: C.cyan,
                animation: 'text-glow 4s ease-in-out infinite'
              }}
            >
              Physiothérapie respiratoire
            </p>
            
            <h1 
              className="text-4xl sm:text-5xl lg:text-7xl leading-[1.1] mb-8 tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.white }}
            >
              La rééducation des troubles{' '}
              <span 
                className="font-normal"
                style={{ color: '#0EA5E9' }}
              >
                respiratoires
              </span>
            </h1>
            
            <div 
              className="text-base lg:text-lg leading-relaxed mb-12 max-w-4xl mx-auto text-left space-y-4"
              style={{ color: C.textSecondary }}
            >
              <p>
                Vous souffrez d'hyperventilation, de respiration dysfonctionnelle, d'asthme, ou de symptômes liés au COVID Long ?
              </p>
              <p>
                Ce site est avant tout destiné aux patients du cabinet, comme un support succinct pour les aider à intégrer les notions essentielles de base, nécessaires à la pratique. Le sujet de la respiration est un domaine extrêmement complexe qui implique de nombreuses dimensions de notre biologie, de notre mental et de nos émotions, du système nerveux, notre alimentation, notre sommeil, notre contexte individuel de vie, notre mobilité, force, capacité de relaxation etc...
              </p>
              <p>
                Il est donc totalement impossible d'être complet et ce n'est donc pas l'objectif de ce projet.
              </p>
              <p>
                Vous trouverez sur ces pages quelques notions théoriques de base, des moyens d'évaluer votre respiration, des documents à télécharger, des vidéos réalisées en séance avec des patients pour la pratique à domicile, et une application complète d'exercices et des défis respiratoires.
              </p>
              <p 
                className="italic"
                style={{ 
                  color: C.cyanLight,
                  animation: 'text-glow 6s ease-in-out infinite'
                }}
              >
                Le travail sur la respiration demande un investissement total en termes de temps, de répétition, de conscientisation, et aboutit à un changement progressif, doux, dans le respect du corps, mais total, et des résultats inimaginables en termes d'amélioration de la santé.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ ABOUT ME SECTION ============ */}
      <section className="pt-6 pb-24 relative overflow-hidden z-10">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 relative z-10">
          <div className="max-w-3xl mx-auto">
            {/* Brand logo — placed in the space between the previous section and the About text */}
            <div className="flex justify-center mb-6" data-testid="about-brand-logo-wrap">
              <img
                src="https://customer-assets-lqy194kg.emergentagent.net/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/60e8gwgv_ChatGPT%20Image%2021%20ao%C3%BBt%202026%2C%2008_05_27.png"
                alt="Physiothérapie Les Mains Nuages"
                className="w-full max-w-lg h-auto rounded-2xl"
                data-testid="about-brand-logo"
              />
            </div>

            {/* Content */}
            <div>
              <p 
                className="text-xs md:text-sm uppercase tracking-[0.2em] mb-4 font-semibold"
                style={{ color: C.cyan }}
              >
                À propos
              </p>
              
              <div className="space-y-5 text-base leading-relaxed" style={{ color: C.textSecondary }}>
                <p>
                  J'exerce la physiothérapie depuis 1999 – depuis mes débuts, j'ai ressenti que les principes dictés par la conception de la santé dans ce domaine ne me parlaient pas, il y avait quelque chose qui me dérangeait profondément dans la façon d'envisager les pathologies, de soigner des symptômes basés sur des protocoles identiques pour tous, avec des moyens (machines de renforcement, thérapies avec des appareils électriques etc…) qui me paraissaient aller à l'encontre du fonctionnement naturel, biologique, du corps humain.
                </p>
                <p>
                  Je me suis toujours, comme dans la vie quotidienne d'ailleurs, laisser naturellement guider par ce que je ressens, plutôt que par des théories « écrites dans le marbre », j'ai donc suivi cette intuition et, ai découvert le principe des « Chaines Physiologiques » de L. Busquet, auxquelles je me suis formé, et ce fut une véritable révélation : on avait là une véritable compréhension des logiques parfaites de l'anatomie du corps, et de l'intelligence exceptionnelle de son fonctionnement, qui permettait de comprendre et de traiter intelligemment, grâce à une lecture enfin claire et logique de ce que nous pensons être des dysfonctionnement, et qui ne sont en fait que des adaptations tout-à-fait cohérentes de l'organisme aux différents stresseurs auxquels il est constamment soumis.
                </p>
                <p className="text-white font-medium">
                  La passion de soigner pouvait à présent s'exprimer avec enthousiasme, et grandissait jour après jour !
                </p>
                <p>
                  Des années plus tard, j'ai découvert le monde des problématiques respiratoires, qui m'a instantanément passionné, et je me suis lancé dans une quête de compréhension qui a duré cinq ans, passant de lectures en formations et surtout de centaines d'heures de pratique personnelle pour comprendre, dans mon corps, les éléments théoriques étudiés.
                </p>
                <p>
                  Cette pratique m'a profondément transformé, dans ma structure corporelle, dans mon système nerveux, dans mon mental, et, par conséquent, au niveau de ma vie quotidienne dans tous ses aspects.
                </p>
                <p>
                  J'ai découvert <span className="text-white font-medium">la puissance de la respiration</span>, mais aussi son interdépendance totale avec tous les systèmes de l'organisme, et ma vie a changé, ma façon de comprendre mes patients, et surtout de pouvoir leur faire comprendre que leurs symptômes n'ont jamais été des problèmes à régler, mais des signes que leur corps s'adaptait de manière extrêmement intelligente aux stresseurs auxquels il était soumis.
                </p>
                <p>
                  Le système que je propose aujourd'hui, est basé sur ces fondements : et les solutions sont logiques, simples, et faciles à mettre en œuvre. <span className="text-white font-medium">Les effets sur notre corps, notre mental, notre vie entière, sont inimaginables.</span>
                </p>
                <p className="italic text-cyan-300">
                  La seule question à se poser est : suis-je prêt-e à me débarrasser des fardeaux qui m'empêchent de ressentir la joie de vivre à chaque instant, et à vivre, non pas comme un chêne : lourd, rigide et cassant, mais comme un bambou : solidement enraciné, souple, malléable, léger et en constante expansion…
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ WARNING SECTION ============ */}
      <section className="py-12 z-10 relative">
        <div className="max-w-4xl mx-auto px-6 sm:px-12">
          <GlassCard hover={false} className="p-8">
            <div className="flex items-start gap-4">
              <div 
                className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 bg-amber-500/20"
              >
                <AlertCircle className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h3 
                  className="text-lg font-medium mb-2 text-white"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Avertissement
                </h3>
                <p className="text-base leading-relaxed" style={{ color: C.textSecondary }}>
                  Les informations et les exercices décrits sur ces pages sont à titre informatifs ; les problématiques auxquelles elles s'adressent relèvent d'un diagnostic médical et d'un accompagnement par un professionnel de santé spécialisé. Ces pages sont un résumé très succinct du contenu des séances thérapeutiques que j'effectue selon une progression totalement individualisée par un bilan complet et une connaissance précise de la personne. N'hésitez pas à me contacter pour de plus amples informations.
                </p>
              </div>
            </div>
          </GlassCard>
        </div>
      </section>

      {/* ============ CONDITIONS SECTION ============ */}
      <section className="py-24 z-10 relative">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-16">
            <p 
              className="text-xs md:text-sm uppercase tracking-[0.2em] mb-4 font-semibold"
              style={{ color: C.cyan }}
            >
              Troubles traités
            </p>
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl mb-6 tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.white }}
            >
              Troubles Respiratoires <span className="font-normal bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-400">Traités</span>
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: C.textSecondary }}>
              Une approche personnalisée pour chaque condition, basée sur les dernières avancées en physiothérapie respiratoire.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {conditions.map((condition, index) => (
              <GlassCard
                key={index}
                onClick={() => navigate(condition.link)}
                className="p-8"
              >
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 bg-cyan-500/20"
                >
                  <condition.icon size={24} strokeWidth={1.5} className="text-cyan-400" />
                </div>
                <h3 
                  className="text-xl mb-3 text-white"
                  style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400 }}
                >
                  {condition.title}
                </h3>
                <p className="text-sm leading-relaxed mb-4" style={{ color: C.textSecondary }}>
                  {condition.description}
                </p>
                <span 
                  className="inline-flex items-center gap-1 text-sm font-medium transition-all hover:gap-2 text-cyan-400"
                >
                  En savoir plus <ChevronRight size={16} />
                </span>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHY SECTION ============ */}
      <section className="py-24 relative overflow-hidden z-10">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p 
                className="text-xs md:text-sm uppercase tracking-[0.2em] mb-4 font-semibold"
                style={{ color: C.cyan }}
              >
                Pourquoi
              </p>
              <h2 
                className="text-3xl sm:text-4xl lg:text-5xl mb-8 tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.white }}
              >
                Pourquoi la Rééducation <span className="font-normal bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-400">Respiratoire</span> ?
              </h2>
              
              {/* TEXTE ORIGINAL CONSERVÉ À 100% */}
              <div className="space-y-6" style={{ color: C.textSecondary }}>
                <p className="text-lg leading-relaxed">
                  La respiration est une fonction vitale que nous effectuons plus de 20 000 fois par jour. 
                  Pourtant, de nombreuses personnes développent des patterns respiratoires inadaptés qui peuvent 
                  entraîner une multitude de symptômes physiques et psychologiques.
                </p>
                <p className="text-lg leading-relaxed">
                  En tant que physiothérapeute spécialisé, je vous accompagne dans la correction de ces dysfonctionnements 
                  à travers des exercices ciblés et des techniques éprouvées, notamment la méthode Buteyko et la cohérence cardiaque.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <GlassCard 
                  key={index} 
                  hover={false}
                  className="flex items-center gap-3 p-5"
                >
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-cyan-500/20"
                  >
                    <CheckCircle className="w-4 h-4 text-cyan-400" />
                  </div>
                  <span className="text-sm" style={{ color: C.textSecondary }}>{benefit}</span>
                </GlassCard>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ RESOURCES SECTION ============ */}
      <section className="py-24 z-10 relative">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-16">
            <p 
              className="text-xs md:text-sm uppercase tracking-[0.2em] mb-4 font-semibold"
              style={{ color: C.cyan }}
            >
              Ressources
            </p>
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl mb-6 tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.white }}
            >
              Comprendre Votre <span className="font-normal bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-400">Respiration</span>
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: C.textSecondary }}>
              Explorez les ressources éducatives pour mieux comprendre le fonctionnement de votre système respiratoire.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {educationalResources.map((resource, index) => (
              <GlassCard
                key={index}
                onClick={() => navigate(resource.link)}
                className="p-8"
              >
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 bg-cyan-500/20"
                >
                  <resource.icon size={24} strokeWidth={1.5} className="text-cyan-400" />
                </div>
                <h3 
                  className="text-xl mb-3 text-white"
                  style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400 }}
                >
                  {resource.title}
                </h3>
                <p className="text-sm leading-relaxed mb-4" style={{ color: C.textSecondary }}>
                  {resource.description}
                </p>
                <span 
                  className="inline-flex items-center gap-1 text-sm font-medium transition-all hover:gap-2 text-cyan-400"
                >
                  Découvrir <ChevronRight size={16} />
                </span>
              </GlassCard>
            ))}
          </div>

          {/* Extra buttons */}
          <div className="flex flex-wrap justify-center gap-4 mt-12">
            <button
              onClick={() => navigate('/youtube')}
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-base font-medium transition-all duration-500 bg-red-500/10 backdrop-blur-xl text-red-300 border border-red-400/30 shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.1)] hover:bg-red-500/20 hover:border-red-400/50 hover:shadow-[0_8px_40px_rgba(239,68,68,0.25),inset_0_1px_0_rgba(255,255,255,0.15)] hover:-translate-y-1"
            >
              <Youtube size={20} />
              Chaîne YouTube
            </button>
            <button
              onClick={() => navigate('/documents?open=theorie')}
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-base font-medium transition-all duration-500 bg-white/5 backdrop-blur-xl text-cyan-300 border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.1)] hover:bg-white/10 hover:border-cyan-400/40 hover:shadow-[0_8px_40px_rgba(6,182,212,0.2),inset_0_1px_0_rgba(255,255,255,0.15)] hover:-translate-y-1"
            >
              <ClipboardList size={20} />
              La Rééducation, document complet
            </button>
          </div>
        </div>
      </section>

      {/* ============ REVIEWS SECTION ============ */}
      <ReviewsSection />

      {/* ============ CTA SECTION ============ */}
      <section className="py-24 relative overflow-hidden z-10">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center relative z-10">
          <GlassCard hover={false} className="p-12 md:p-16">
            <Droplets className="w-16 h-16 mx-auto mb-6 text-cyan-400 opacity-60" />
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl mb-6 tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.white }}
            >
              Prêt à Améliorer Votre <span className="font-normal bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-400">Respiration</span> ?
            </h2>
            <p className="text-lg mb-10 max-w-2xl mx-auto" style={{ color: C.textSecondary }}>
              Accédez gratuitement à nos exercices de rééducation respiratoire et commencez votre parcours vers une meilleure santé.
            </p>
            <button
              onClick={() => navigate('/app-landing')}
              className="inline-flex items-center gap-3 px-10 py-5 rounded-full text-lg font-medium transition-all duration-500 bg-cyan-400/20 backdrop-blur-xl text-cyan-100 border border-cyan-400/50 shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.15)] hover:bg-cyan-400/30 hover:border-cyan-300/70 hover:shadow-[0_8px_40px_rgba(6,182,212,0.4),inset_0_1px_0_rgba(255,255,255,0.2)] hover:-translate-y-1"
              data-testid="cta-exercises"
            >
              Commencer maintenant
              <ArrowRight size={20} />
            </button>
          </GlassCard>
        </div>
      </section>

      <SiteFooterV2 />
    </div>
  );
};

export default HomePageV2;
